/**
 * Presentation-only focused workflow for Repair Orders.
 * Status transitions, permissions, timers and notifications stay on the server.
 * Stage ids are a view over the existing repair-order statuses.
 */

import { JOB_STATUS_LABELS, JOB_STATUS_ORDER } from "./serviceConstants";
import { hasRoleCapability, type UserRoleFields } from "./userRoles";

export const RO_FOCUSED_STAGES = [
  {
    id: "intake",
    label: "Intake",
    description: "Receive the equipment and prepare it for diagnosis",
  },
  {
    id: "diagnosis",
    label: "Diagnosis",
    description: "Assess the fault and recommend a repair",
  },
  {
    id: "approval",
    label: "Customer Approval",
    description: "Confirm the price, then record or request customer approval",
  },
  {
    id: "repair",
    label: "Repair",
    description: "Carry out the approved repair",
  },
  {
    id: "finance",
    label: "Finance",
    description: "Invoice and collect payment",
  },
  {
    id: "collection",
    label: "Collection / Close",
    description: "Arrange collection or delivery and close the repair",
  },
] as const;

export type RoFocusedStageId = (typeof RO_FOCUSED_STAGES)[number]["id"];

export type RoFocusedPrimaryKind =
  | "start"
  | "claim"
  | "admin-start"
  | "focus-approval"
  | "focus-diagnosis"
  | "focus-finance"
  | "focus-collection"
  | "none";

export type RoStartMode = "start" | "claim" | "admin-start";

export type RoStartDecision =
  | {
      kind: "start";
      mode: RoStartMode;
      /** Written before the timer when the current status is not yet a timed work state. */
      nextStatus: "diagnosis" | null;
      reassign: boolean;
    }
  | {
      kind: "blocked";
      code: "BUSINESS" | "ASSIGNMENT" | "ROLE";
      message: string;
    };

export type RoFocusedWorkflowInput = {
  status: string | null | undefined;
  assignedTechnicianId: number | null;
  assignedTechnicianName?: string | null;
  viewer: (UserRoleFields & { userId?: number | null }) | null | undefined;
  ownTimerRunning: boolean;
};

export type RoFocusedWorkflowView = {
  stageId: RoFocusedStageId;
  stageLabel: string;
  headerBadge: string;
  currentStage: string;
  completed: string[];
  nextEyebrow: string;
  nextTitle: string;
  nextDetail: string;
  primaryLabel: string | null;
  primaryKind: RoFocusedPrimaryKind;
  blocker: string | null;
  workflowComplete: boolean;
};

const STATUS_STAGE: Record<string, RoFocusedStageId> = {
  received: "intake",
  diagnosis: "diagnosis",
  pending_finance: "diagnosis",
  awaiting_commercial_review: "approval",
  ready_to_send_to_customer: "approval",
  awaiting_customer_confirmation: "approval",
  customer_declined: "approval",
  in_progress: "repair",
  pending_parts: "repair",
  repair_complete: "finance",
  awaiting_payment: "finance",
  awaiting_collection: "collection",
  closed: "collection",
};

function statusKey(status: string | null | undefined): string {
  return String(status ?? "").trim().toLowerCase();
}

export function roFocusedStageId(status: string | null | undefined): RoFocusedStageId {
  return STATUS_STAGE[statusKey(status)] ?? "intake";
}

export function canOperateRepairWork(viewer: UserRoleFields | null | undefined): boolean {
  return hasRoleCapability(viewer, ["admin", "service"]);
}

function isAdmin(viewer: UserRoleFields | null | undefined): boolean {
  return hasRoleCapability(viewer, ["admin"]);
}

function assigneeFirstName(name: string | null | undefined): string {
  const trimmed = String(name ?? "").trim();
  if (!trimmed) return "another technician";
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

function businessBlock(status: string): string | null {
  switch (status) {
    case "pending_finance":
      return "Finance must enter the parts cost before diagnosis can continue.";
    case "awaiting_commercial_review":
      return "Admin or Finance must confirm the selling price. The assigned technician can separately start without approval with an audited reason.";
    case "ready_to_send_to_customer":
      return "The PIC must record or request the customer decision. Starting before approval requires an explicit technician exception.";
    case "awaiting_customer_confirmation":
      return "Customer approval is outstanding. The assigned technician can explicitly start without approval with an audited reason.";
    case "customer_declined":
      return "The customer declined this repair. Work cannot start.";
    case "repair_complete":
    case "awaiting_payment":
    case "awaiting_collection":
      return "This repair is past the work stage. Continue in Finance or collection.";
    case "closed":
      return "This repair order is closed.";
    default:
      return null;
  }
}

/**
 * Server and client share this decision.
 * Claim confirmation is enforced by the server when the decision says `reassign`.
 */
export function resolveRoStartWork(input: {
  status: string | null | undefined;
  assignedTechnicianId: number | null;
  viewer: (UserRoleFields & { userId?: number | null }) | null | undefined;
}): RoStartDecision {
  const status = statusKey(input.status);
  const blocked = businessBlock(status);
  if (blocked) return { kind: "blocked", code: "BUSINESS", message: blocked };
  if (status !== "received" && status !== "diagnosis" && status !== "in_progress" && status !== "pending_parts") {
    return {
      kind: "blocked",
      code: "BUSINESS",
      message: "Work cannot begin until the repair has been approved to proceed.",
    };
  }
  if (!input.viewer?.userId || !canOperateRepairWork(input.viewer)) {
    return {
      kind: "blocked",
      code: "ROLE",
      message: "Only a service technician or Admin can start work on this repair order.",
    };
  }

  const nextStatus = status === "received" ? "diagnosis" : null;
  const assignedId = input.assignedTechnicianId;
  if (assignedId == null) {
    return { kind: "start", mode: "claim", nextStatus, reassign: true };
  }
  const isSelf = assignedId === input.viewer.userId;
  if (!isSelf && !isAdmin(input.viewer)) {
    return {
      kind: "blocked",
      code: "ASSIGNMENT",
      message: "This repair is currently assigned to another technician.",
    };
  }
  if (!isSelf && isAdmin(input.viewer)) {
    return { kind: "start", mode: "admin-start", nextStatus, reassign: false };
  }
  return { kind: "start", mode: "start", nextStatus, reassign: false };
}

export function buildRoFocusedWorkflow(input: RoFocusedWorkflowInput): RoFocusedWorkflowView {
  const status = statusKey(input.status);
  const stageId = roFocusedStageId(status);
  const stage = RO_FOCUSED_STAGES.find((item) => item.id === stageId) ?? RO_FOCUSED_STAGES[0];
  const badge = JOB_STATUS_LABELS[status] ?? "Received";
  const stageIndex = RO_FOCUSED_STAGES.findIndex((item) => item.id === stageId);
  const workflowComplete = status === "closed";
  const completed = workflowComplete
    ? RO_FOCUSED_STAGES.map((item) => item.label)
    : RO_FOCUSED_STAGES.slice(0, Math.max(stageIndex, 0)).map((item) => item.label);
  const assignee = String(input.assignedTechnicianName ?? "").trim();
  const decision = resolveRoStartWork(input);
  const running = input.ownTimerRunning;

  let currentStage: string = stage.label;
  let nextTitle = "Continue this repair";
  let nextDetail = "Open the current stage and complete the next recorded step.";
  let primaryLabel: string | null = null;
  let primaryKind: RoFocusedPrimaryKind = "none";
  let blocker: string | null = decision.kind === "blocked" ? decision.message : null;

  if (status === "received") {
    currentStage = "Ready for diagnosis";
    nextTitle = "Begin diagnosis";
    nextDetail = assignee
      ? `Assigned to ${assignee}. Start work when diagnosis begins.`
      : "This repair has been received and is ready to be assessed.";
  } else if (status === "diagnosis") {
    currentStage = "Assessing the fault";
    nextTitle = "Continue diagnosis";
    nextDetail = "Diagnosis is open. Start the work timer while the fault is assessed.";
  } else if (status === "pending_finance") {
    currentStage = "Waiting for parts cost";
    nextTitle = "Waiting for parts cost";
    nextDetail = "Finance needs to enter the parts cost before the proposal can continue.";
    primaryLabel = "ENTER PARTS COST";
    primaryKind = "focus-diagnosis";
  } else if (status === "awaiting_commercial_review") {
    currentStage = "Commercial review";
    nextTitle = "Confirm the selling price";
    nextDetail = "Admin or Finance must confirm the customer selling price before the proposal can be sent.";
    primaryLabel = "REVIEW PROPOSAL";
    primaryKind = "focus-approval";
  } else if (status === "ready_to_send_to_customer") {
    currentStage = "Ready to send";
    nextTitle = "Record or request customer approval";
    nextDetail = "The price is confirmed. The assigned PIC can approve on behalf of the customer or send an approval request. The assigned technician may explicitly start without approval; the customer decision remains outstanding.";
    primaryLabel = "REVIEW PROPOSAL";
    primaryKind = "focus-approval";
  } else if (status === "awaiting_customer_confirmation") {
    currentStage = "Waiting for customer approval";
    nextTitle = "Waiting for customer approval";
    nextDetail = "The proposal awaits the customer decision. A technician exception can start work without recording approval.";
    primaryLabel = "REVIEW PROPOSAL";
    primaryKind = "focus-approval";
  } else if (status === "customer_declined") {
    currentStage = "Customer declined";
    nextTitle = "Customer declined the repair";
    nextDetail = "The customer declined this repair. PIC follow-up is required. Repair work cannot start.";
    primaryLabel = "REVIEW PROPOSAL";
    primaryKind = "focus-approval";
  } else if (status === "in_progress") {
    currentStage = "Ready to repair";
    nextTitle = "Begin repair";
    nextDetail = "Customer approval has been recorded. Start the work timer when repair begins.";
  } else if (status === "pending_parts") {
    currentStage = "Waiting for parts";
    nextTitle = "Waiting for parts";
    nextDetail = "Parts are still required. Start a work session when there is legitimate work to do on this repair.";
  } else if (status === "repair_complete") {
    currentStage = "Invoice required";
    nextTitle = "Raise the invoice";
    nextDetail = "The repair is complete. Finance needs to invoice the customer before collection.";
    primaryLabel = "OPEN FINANCE";
    primaryKind = "focus-finance";
  } else if (status === "awaiting_payment") {
    currentStage = "Awaiting payment";
    nextTitle = "Collect payment";
    nextDetail = "The invoice is with the customer. Record payment when it arrives.";
    primaryLabel = "OPEN FINANCE";
    primaryKind = "focus-finance";
  } else if (status === "awaiting_collection") {
    currentStage = "Awaiting collection";
    nextTitle = "Arrange collection";
    nextDetail = "Payment is recorded. Arrange pickup or delivery, then close the repair order.";
    primaryLabel = "OPEN COLLECTION";
    primaryKind = "focus-collection";
  } else if (status === "closed") {
    currentStage = "Completed";
    nextTitle = "Repair order closed";
    nextDetail = "Intake, repair, finance, and collection are finished.";
    blocker = null;
  }

  if (decision.kind === "blocked" && decision.code === "ASSIGNMENT") {
    nextTitle = assignee ? `Assigned to ${assignee}` : "Assigned to another technician";
    nextDetail = `This repair is currently assigned to ${assigneeFirstName(assignee)}.`;
    primaryLabel = null;
    primaryKind = "none";
    blocker = decision.message;
  } else if (decision.kind === "blocked" && decision.code === "ROLE" && (status === "received" || status === "diagnosis" || status === "in_progress" || status === "pending_parts")) {
    blocker = decision.message;
    primaryLabel = null;
    primaryKind = "none";
  } else if (decision.kind === "start") {
    blocker = null;
    primaryKind = decision.mode;
    primaryLabel = "START WORK";
    if (decision.mode === "admin-start" && assignee) {
      nextTitle = `Assigned to ${assignee}`;
      nextDetail = `This repair is currently assigned to ${assigneeFirstName(assignee)}. Starting work records your timer and leaves ${assigneeFirstName(assignee)} assigned.`;
    }
  }

  if (
    primaryKind === "focus-approval" ||
    primaryKind === "focus-diagnosis" ||
    primaryKind === "focus-finance" ||
    primaryKind === "focus-collection"
  ) {
    blocker = null;
  }

  if (running) {
    currentStage = stageId === "diagnosis" || stageId === "intake" ? "Diagnosis in progress" : "Repair in progress";
    nextTitle = currentStage;
    nextDetail = "The work timer is running. Stop work when this session ends.";
    primaryLabel = null;
    primaryKind = "none";
    blocker = null;
  }

  return {
    stageId,
    stageLabel: stage.label,
    headerBadge: badge,
    currentStage,
    completed,
    nextEyebrow: running ? currentStage : "Next action",
    nextTitle,
    nextDetail,
    primaryLabel,
    primaryKind,
    blocker,
    workflowComplete,
  };
}

/** Every stored repair-order status has one focused stage. */
export function everyRepairStatusHasFocusedStage(): boolean {
  return JOB_STATUS_ORDER.every((status) => STATUS_STAGE[status] != null);
}
